// ============================================================
// Platform Control · Realtime — live-updating surfaces,
// publication status, and subscription truth. Shows which tables
// are published to Supabase Realtime and which UI surfaces
// subscribe.
// ============================================================

import { useNavigate } from 'react-router-dom'
import ViewShell, { Card, Row, Section } from './_ViewShell'
import { SHELL_TOKENS } from '../../../shell/styles'

const PUBLISHED_TABLES = [
  { table: 'olv_audit_trail',          purpose: 'OLIVIUM signal feed',          subscriber: 'Audit Logs, OLIVIUM Feed' },
  { table: 'sq_workspace_runs',        purpose: 'Workspace execution state',    subscriber: 'Studio' },
  { table: 'sq_workspace_agent_events', purpose: 'Agent event stream',          subscriber: 'Studio' },
  { table: 'sq_workspace_sections',    purpose: 'Content section updates',      subscriber: 'Studio' },
  { table: 'sq_site_deployments',      purpose: 'Site deploy status',           subscriber: 'Site Builder, Hosting' },
  { table: 'sq_deployments',           purpose: 'Hosting deploy status',        subscriber: 'Hosting' },
  { table: 'sq_domains',              purpose: 'Domain verification status',   subscriber: 'Hosting' },
  { table: 'sq_commands',             purpose: 'Command dispatch',             subscriber: 'Command Bar' },
  { table: 'sc_workflow_executions',   purpose: 'Workflow execution state',     subscriber: 'Workflows' },
  { table: 'sc_workflow_step_runs',    purpose: 'Step-level execution state',   subscriber: 'Workflows' },
  { table: 'sq_integrations',         purpose: 'Integration status changes',   subscriber: 'Integrations' },
  { table: 'sq_webhooks',             purpose: 'Webhook status changes',       subscriber: 'Webhooks' },
]

const MANUAL_REFRESH_SURFACES = [
  { surface: 'Workflow detail drawer', reason: 'No Realtime subscription yet — manual page reload' },
  { surface: 'Project Settings', reason: 'Standard fetch-on-mount' },
  { surface: 'Support Requests', reason: 'Standard fetch-on-mount' },
]

export default function RealtimeView() {
  const navigate = useNavigate()

  return (
    <ViewShell
      kicker="PLATFORM CONTROL · REALTIME"
      title="Realtime"
      description="Live-updating surfaces, publication status, and subscription truth."
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => navigate('/square/command-center/operations')}
            style={navBtnStyle}
          >
            Open Workflows →
          </button>
          <button
            type="button"
            onClick={() => navigate('/square/olivium/feed')}
            style={navBtnStyle}
          >
            Open OLIVIUM →
          </button>
        </div>
      }
    >
      <Section label="PUBLISHED TABLES">
        <Card>
          {PUBLISHED_TABLES.map((t) => (
            <Row
              key={t.table}
              label={t.table}
              value="published"
              hint={`${t.purpose} → ${t.subscriber}`}
            />
          ))}
        </Card>
      </Section>

      <Section label="MANUAL REFRESH SURFACES">
        <Card>
          {MANUAL_REFRESH_SURFACES.map((s) => (
            <Row key={s.surface} label={s.surface} value="manual" hint={s.reason} />
          ))}
        </Card>
      </Section>

      <Section label="INFRASTRUCTURE">
        <Card>
          <Row label="Provider" value="Supabase Realtime" hint="WebSocket-based postgres_changes" />
          <Row label="Publication" value="supabase_realtime" hint="managed by Supabase" />
          <Row label="Presence" value="not yet wired" hint="presence dashboard deferred" />
        </Card>
      </Section>
    </ViewShell>
  )
}

const navBtnStyle: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 10,
  border: `1px solid ${SHELL_TOKENS.border}`,
  background: 'transparent',
  color: SHELL_TOKENS.ink,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  fontFamily: 'var(--font-mono)',
  cursor: 'pointer',
}
